// src/features/calendar/components/EventDetailModal.tsx
import React, { useEffect, useState } from 'react';
import { useCalendarStore } from '../store/calendarStore';
import { useCalendarApi } from '../hooks/useCalendarApi';
import type { CalendarEvent, EdgeCalendar } from '../types/calendar.types';
import type { MatterDetailed } from '../../../types/matters';
import { FolderIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { fetchMailboxAccounts } from '@/features/edgemail-client/services/MailClientAPI';
import type { MailAccount } from '@/features/edgemail-client/types/mail.types';

interface EventDetailModalProps {
  event: CalendarEvent;
  isOpen: boolean;
  onClose: () => void;
  onEdit: () => void;
  matter?: MatterDetailed;
  onBackToMatter?: () => void;
}

type RsvpStatus = 'pending' | 'accepted' | 'declined' | 'tentative';

interface EventAttendeeViewModel {
  email: string;
  name: string;
  status: RsvpStatus;
  isOrganizer: boolean;
  responseTime?: Date;
  responseNote?: string;
}

const normalizeAttendees = (attendees: unknown): EventAttendeeViewModel[] => {
  if (!Array.isArray(attendees)) return [];

  return attendees.reduce<EventAttendeeViewModel[]>((normalized, raw: any) => {
      const email = String(raw?.email || '').trim();
      if (!email) return normalized;

      const status = (raw?.status || 'pending') as RsvpStatus;
      normalized.push({
        email,
        name: String(raw?.name || email),
        status,
        isOrganizer: Boolean(raw?.is_organizer ?? raw?.isOrganizer ?? false),
        responseTime: raw?.response_time
          ? new Date(raw.response_time)
          : raw?.responseTime
            ? new Date(raw.responseTime)
            : undefined,
        responseNote: raw?.response_note ?? raw?.responseNote,
      });
      return normalized;
    }, []);
};

const mergeAttendees = (
  current: EventAttendeeViewModel[],
  incoming: EventAttendeeViewModel[],
): EventAttendeeViewModel[] => {
  const merged = new Map<string, EventAttendeeViewModel>();

  for (const attendee of current) {
    const email = attendee.email.trim().toLowerCase();
    if (!email) continue;
    merged.set(email, attendee);
  }

  for (const attendee of incoming) {
    const email = attendee.email.trim().toLowerCase();
    if (!email) continue;
    const existing = merged.get(email);
    if (!existing) {
      merged.set(email, attendee);
      continue;
    }

    merged.set(email, {
      ...existing,
      ...attendee,
      name: attendee.name || existing.name,
      isOrganizer: attendee.isOrganizer ?? existing.isOrganizer,
      status: attendee.status || existing.status,
      responseNote: attendee.responseNote ?? existing.responseNote,
      responseTime: attendee.responseTime ?? existing.responseTime,
    });
  }

  return Array.from(merged.values());
};

const parseRecipients = (input: string): Array<{ email: string; name: string }> => {
  return input
    .split(/[;,\n]/)
    .map((value) => value.trim())
    .filter(Boolean)
    .map((email) => ({ email, name: email }));
};

const rsvpBadgeClass = (status: RsvpStatus): string => {
  switch (status) {
    case 'accepted':
      return 'text-green-700 bg-green-100 dark:text-green-300 dark:bg-green-900/30';
    case 'declined':
      return 'text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-900/30';
    case 'tentative':
      return 'text-amber-700 bg-amber-100 dark:text-amber-300 dark:bg-amber-900/30';
    default:
      return 'text-gray-700 bg-gray-100 dark:text-gray-300 dark:bg-gray-700';
  }
};

const EventDetailModal: React.FC<EventDetailModalProps> = ({
  event,
  isOpen,
  onClose,
  onEdit,
  matter,
  onBackToMatter,
}) => {
  const { calendars } = useCalendarStore();
  const {
    deleteEvent: deleteEventApi,
    sendEventInvites: sendEventInvitesApi,
    updateAttendeeRsvp: updateAttendeeRsvpApi,
  } = useCalendarApi();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [mailboxes, setMailboxes] = useState<MailAccount[]>([]);
  const [mailboxesLoading, setMailboxesLoading] = useState(false);
  const [selectedMailboxId, setSelectedMailboxId] = useState('');
  const [toRecipients, setToRecipients] = useState('');
  const [ccRecipients, setCcRecipients] = useState('');
  const [bccRecipients, setBccRecipients] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteFeedback, setInviteFeedback] = useState<string | null>(null);
  const [isSendingInvites, setIsSendingInvites] = useState(false);

  const [attendees, setAttendees] = useState<EventAttendeeViewModel[]>(() => normalizeAttendees(event.attendees));
  const [manualRsvpEmail, setManualRsvpEmail] = useState('');
  const [manualRsvpStatus, setManualRsvpStatus] = useState<RsvpStatus>('accepted');
  const [manualRsvpNote, setManualRsvpNote] = useState('');
  const [isApplyingRsvp, setIsApplyingRsvp] = useState(false);
  const [rsvpError, setRsvpError] = useState<string | null>(null);
  const [rsvpFeedback, setRsvpFeedback] = useState<string | null>(null);

  const calendar = calendars.find((cal: EdgeCalendar) => cal.id === event.calendarId);

  useEffect(() => {
    setAttendees(normalizeAttendees(event.attendees));
  }, [event.attendees, event.id]);

  useEffect(() => {
    if (!showInviteModal) return;

    let isMounted = true;

    const loadMailboxes = async () => {
      try {
        setMailboxesLoading(true);
        const accounts = await fetchMailboxAccounts();
        if (!isMounted) return;

        setMailboxes(accounts);

        const defaultMailbox = accounts.find((account) => account.isPrimary) || accounts[0];
        if (defaultMailbox) {
          setSelectedMailboxId(defaultMailbox.id);
        }
      } catch (error) {
        if (!isMounted) return;
        setInviteError(error instanceof Error ? error.message : 'Failed to load mailboxes.');
      } finally {
        if (isMounted) {
          setMailboxesLoading(false);
        }
      }
    };

    loadMailboxes();

    return () => {
      isMounted = false;
    };
  }, [showInviteModal]);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await deleteEventApi(event.id);
      onClose();
    } catch (error) {
      console.error('Failed to delete event:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenInviteModal = () => {
    setInviteError(null);
    setInviteFeedback(null);
    setCcRecipients('');
    setBccRecipients('');
    setInviteMessage('');

    const attendeeEmails = attendees.map((attendee) => attendee.email).join(', ');
    setToRecipients(attendeeEmails);
    setShowInviteModal(true);
  };

  const handleSendInvites = async () => {
    if (!selectedMailboxId) {
      setInviteError('Select a mailbox to send invites from.');
      return;
    }

    try {
      setInviteError(null);
      setIsSendingInvites(true);
      const parsedToRecipients = parseRecipients(toRecipients);
      const parsedCcRecipients = parseRecipients(ccRecipients);
      const parsedBccRecipients = parseRecipients(bccRecipients);

      const result = await sendEventInvitesApi(event.id, {
        mailboxId: selectedMailboxId,
        to: parsedToRecipients.length ? parsedToRecipients : undefined,
        cc: parsedCcRecipients.length ? parsedCcRecipients : undefined,
        bcc: parsedBccRecipients.length ? parsedBccRecipients : undefined,
        message: inviteMessage.trim() || undefined,
      });

      const apiAttendees = normalizeAttendees(result.event?.attendees);
      if (apiAttendees.length > 0) {
        setAttendees((previous) => mergeAttendees(previous, apiAttendees));
      } else {
        const fallbackPending = [...parsedToRecipients, ...parsedCcRecipients]
          .map((recipient) => recipient.email.trim().toLowerCase())
          .filter(Boolean)
          .map((email) => ({
            email,
            name: email,
            status: 'pending' as RsvpStatus,
            isOrganizer: false,
          }));
        if (fallbackPending.length > 0) {
          setAttendees((previous) => mergeAttendees(previous, fallbackPending));
        }
      }

      setInviteFeedback(
        result.queueId
          ? `Invites queued successfully (queue: ${result.queueId}).`
          : 'Invites sent successfully.',
      );
      setShowInviteModal(false);
    } catch (error) {
      setInviteError(error instanceof Error ? error.message : 'Failed to send invites.');
    } finally {
      setIsSendingInvites(false);
    }
  };

  const handleApplyRsvp = async (attendeeEmail: string, status: RsvpStatus, responseNote?: string): Promise<boolean> => {
    try {
      setRsvpError(null);
      setRsvpFeedback(null);
      setIsApplyingRsvp(true);

      await updateAttendeeRsvpApi(event.id, {
        attendeeEmail,
        status,
        responseNote: responseNote?.trim() || undefined,
      });

      const normalizedEmail = attendeeEmail.trim().toLowerCase();
      setAttendees((previous) => {
        const index = previous.findIndex((attendee) => attendee.email.trim().toLowerCase() === normalizedEmail);
        const nextAttendee: EventAttendeeViewModel = {
          email: attendeeEmail,
          name: index >= 0 ? previous[index].name : attendeeEmail,
          status,
          isOrganizer: index >= 0 ? previous[index].isOrganizer : false,
          responseTime: new Date(),
          responseNote: responseNote,
        };

        if (index >= 0) {
          const next = [...previous];
          next[index] = {
            ...next[index],
            status,
            responseTime: new Date(),
            responseNote: responseNote,
          };
          return next;
        }

        return [...previous, nextAttendee];
      });

      setRsvpFeedback(`RSVP updated for ${attendeeEmail}.`);
      return true;
    } catch (error) {
      setRsvpError(error instanceof Error ? error.message : 'Failed to update RSVP.');
      return false;
    } finally {
      setIsApplyingRsvp(false);
    }
  };

  const handleManualRsvpSubmit = async () => {
    if (!manualRsvpEmail.trim()) {
      setRsvpError('Attendee email is required.');
      return;
    }

    const success = await handleApplyRsvp(manualRsvpEmail.trim(), manualRsvpStatus, manualRsvpNote);
    if (success) {
      setManualRsvpEmail('');
      setManualRsvpNote('');
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: event.allDay ? undefined : 'numeric',
      minute: event.allDay ? undefined : '2-digit',
      hour12: !event.allDay,
    }).format(date);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/20';
      case 'high':
        return 'text-orange-600 bg-orange-100 dark:text-orange-400 dark:bg-orange-900/20';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20';
      case 'low':
        return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/20';
      default:
        return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20';
    }
  };

  if (!isOpen) return null;

  const pendingAttendees = attendees.filter((attendee) => attendee.status === 'pending');
  const respondedAttendees = attendees.filter((attendee) => attendee.status !== 'pending');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: calendar?.color || '#3B82F6' }} />
              <span className="text-sm text-gray-500 dark:text-gray-400">{calendar?.name || 'Unknown Calendar'}</span>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{event.title}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 ml-4"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {inviteFeedback && (
          <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
            {inviteFeedback}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <div className="flex items-center space-x-2 mb-1">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Date &amp; Time</span>
            </div>
            <div className="text-gray-900 dark:text-white">
              <div>{formatDate(event.start)}</div>
              {!event.allDay && event.start.getTime() !== event.end.getTime() && (
                <div className="text-sm text-gray-500 dark:text-gray-400">to {formatDate(event.end)}</div>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center space-x-2 mb-1">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10M7 4l-1 16h12L17 4H7z" />
              </svg>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Priority</span>
            </div>
            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full capitalize ${getPriorityColor(event.priority)}`}>
              {event.priority}
            </span>
          </div>

          {event.location && (
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Location</span>
              </div>
              <div className="text-gray-900 dark:text-white">{event.location}</div>
            </div>
          )}

          {event.description && (
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                </svg>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Description</span>
              </div>
              <div className="text-gray-900 dark:text-white whitespace-pre-wrap">{event.description}</div>
            </div>
          )}

          {(event.matterId || (event.contactIds && event.contactIds.length > 0)) && (
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Related Items</span>
              </div>
              <div className="space-y-1">
                {event.matterId && (
                  <div className="text-sm text-lex-accent hover:underline cursor-pointer">Matter: {event.matterId}</div>
                )}
                {event.contactIds?.map((contactId) => (
                  <div key={contactId} className="text-sm text-lex-accent hover:underline cursor-pointer">
                    Contact: {contactId}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Attendees &amp; RSVP</h3>
              <button
                onClick={handleOpenInviteModal}
                className="px-3 py-1.5 text-xs font-medium bg-lex-accent hover:bg-lex-accent-hover text-lex-accent-contrast rounded-md transition-colors"
              >
                Send Invites
              </button>
            </div>

            {attendees.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                No attendees yet. Add or update RSVP by email below.
              </p>
            ) : (
              <div className="space-y-2 mb-4">
                <div className="rounded-md border border-lex-accent bg-lex-accent-soft px-3 py-2 text-xs text-lex-accent">
                  {pendingAttendees.length} pending, {respondedAttendees.length} responded.
                </div>

                {pendingAttendees.length > 0 && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900/40 dark:bg-amber-900/20">
                    <div className="text-xs font-medium text-amber-800 dark:text-amber-300 mb-1">Awaiting Response</div>
                    <div className="flex flex-wrap gap-2">
                      {pendingAttendees.map((attendee) => (
                        <span
                          key={`pending-${attendee.email}`}
                          className="inline-flex items-center rounded-full bg-white/80 px-2 py-1 text-xs text-amber-800 dark:bg-gray-900/40 dark:text-amber-200"
                        >
                          {attendee.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {attendees.map((attendee) => (
                  <div
                    key={attendee.email}
                    className="rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{attendee.name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{attendee.email}</div>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full capitalize ${rsvpBadgeClass(attendee.status)}`}>
                        {attendee.status}
                      </span>
                      {attendee.isOrganizer && (
                        <span className="px-2 py-1 text-xs rounded-full bg-lex-accent-soft text-lex-accent">
                          organizer
                        </span>
                      )}
                    </div>

                    <div className="mt-2 flex flex-wrap gap-2">
                      <button
                        onClick={() => handleApplyRsvp(attendee.email, 'accepted', attendee.responseNote)}
                        className="px-2 py-1 text-xs rounded bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300"
                        disabled={isApplyingRsvp}
                      >
                        Accept
                      </button>
                      <button
                        onClick={() => handleApplyRsvp(attendee.email, 'tentative', attendee.responseNote)}
                        className="px-2 py-1 text-xs rounded bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-300"
                        disabled={isApplyingRsvp}
                      >
                        Tentative
                      </button>
                      <button
                        onClick={() => handleApplyRsvp(attendee.email, 'declined', attendee.responseNote)}
                        className="px-2 py-1 text-xs rounded bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300"
                        disabled={isApplyingRsvp}
                      >
                        Decline
                      </button>
                    </div>

                    {attendee.responseNote && (
                      <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">Note: {attendee.responseNote}</div>
                    )}
                    {attendee.responseTime && (
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                        Updated: {new Date(attendee.responseTime).toLocaleString()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Attendee Email</label>
                <input
                  type="email"
                  value={manualRsvpEmail}
                  onChange={(inputEvent) => setManualRsvpEmail(inputEvent.target.value)}
                  placeholder="attorney@example.com"
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                <select
                  value={manualRsvpStatus}
                  onChange={(inputEvent) => setManualRsvpStatus(inputEvent.target.value as RsvpStatus)}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                >
                  <option value="pending">Pending</option>
                  <option value="accepted">Accepted</option>
                  <option value="tentative">Tentative</option>
                  <option value="declined">Declined</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Response Note</label>
                <input
                  type="text"
                  value={manualRsvpNote}
                  onChange={(inputEvent) => setManualRsvpNote(inputEvent.target.value)}
                  placeholder="Optional note"
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                />
              </div>
            </div>

            {rsvpError && (
              <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {rsvpError}
              </div>
            )}
            {rsvpFeedback && (
              <div className="mt-3 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-700">
                {rsvpFeedback}
              </div>
            )}

            <div className="mt-3 flex justify-end">
              <button
                onClick={handleManualRsvpSubmit}
                disabled={isApplyingRsvp}
                className="px-3 py-2 text-sm font-medium bg-lex-accent hover:bg-lex-accent-hover disabled:bg-lex-accent/50 text-lex-accent-contrast rounded-md transition-colors"
              >
                {isApplyingRsvp ? 'Updating RSVP...' : 'Apply RSVP Update'}
              </button>
            </div>
          </div>

          {matter && (
            <div className="bg-lex-accent-soft border border-lex-accent rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-lex-accent-soft rounded-full flex items-center justify-center">
                    <FolderIcon className="w-4 h-4 text-lex-accent" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-lex-accent">Part of Matter</div>
                    <div className="text-sm text-lex-accent">{matter.title}</div>
                    <div className="text-xs text-lex-accent">
                      {matter.client?.name} • {matter.practice_area}
                    </div>
                  </div>
                </div>
                {onBackToMatter && (
                  <button
                    onClick={onBackToMatter}
                    className="flex items-center gap-2 px-3 py-2 bg-lex-accent hover:bg-lex-accent-hover text-lex-accent-contrast rounded-md text-sm font-medium transition-colors"
                  >
                    <ArrowLeftIcon className="w-4 h-4" />
                    Back to Matter
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between items-center pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="px-3 py-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium"
          >
            Delete Event
          </button>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-lg text-sm font-medium transition-colors"
            >
              Close
            </button>
            <button
              onClick={handleOpenInviteModal}
              className="px-4 py-2 bg-lex-accent hover:bg-lex-accent-hover text-lex-accent-contrast rounded-lg text-sm font-medium transition-colors"
            >
              Send Invites
            </button>
            <button
              onClick={onEdit}
              className="px-4 py-2 bg-lex-accent hover:bg-lex-accent-hover text-lex-accent-contrast rounded-lg text-sm font-medium transition-colors"
            >
              Edit Event
            </button>
          </div>
        </div>

        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-sm mx-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Delete Event</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Are you sure you want to delete &quot;{event.title}&quot;? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-lg text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showInviteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Send Event Invites</h3>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Send From Mailbox</label>
                  <select
                    value={selectedMailboxId}
                    onChange={(inputEvent) => setSelectedMailboxId(inputEvent.target.value)}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                    disabled={mailboxesLoading}
                  >
                    <option value="">Select mailbox...</option>
                    {mailboxes.map((mailbox) => (
                      <option key={mailbox.id} value={mailbox.id}>
                        {mailbox.email}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">To (comma or newline separated)</label>
                  <textarea
                    rows={2}
                    value={toRecipients}
                    onChange={(inputEvent) => setToRecipients(inputEvent.target.value)}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                    placeholder="Leave empty to use all event attendees"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CC</label>
                    <input
                      type="text"
                      value={ccRecipients}
                      onChange={(inputEvent) => setCcRecipients(inputEvent.target.value)}
                      className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">BCC</label>
                    <input
                      type="text"
                      value={bccRecipients}
                      onChange={(inputEvent) => setBccRecipients(inputEvent.target.value)}
                      className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Message (optional)</label>
                  <textarea
                    rows={3}
                    value={inviteMessage}
                    onChange={(inputEvent) => setInviteMessage(inputEvent.target.value)}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              {inviteError && (
                <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {inviteError}
                </div>
              )}

              <div className="mt-5 flex justify-end gap-3">
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-lg text-sm font-medium transition-colors"
                  disabled={isSendingInvites}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendInvites}
                  disabled={isSendingInvites || mailboxesLoading}
                  className="px-4 py-2 bg-lex-accent hover:bg-lex-accent-hover disabled:bg-lex-accent/50 text-lex-accent-contrast rounded-lg text-sm font-medium transition-colors"
                >
                  {isSendingInvites ? 'Sending...' : 'Send Invites'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventDetailModal;
